"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { GoogleLogin } from "@react-oauth/google";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { setAccessToken, removeAccessToken } from "@/app/lib/cookie";
import { api_url } from "@/app/api";
import { METHOD_TYPE, MIDDLEWARE } from "@/app/lib/enums";
import { safeLocalStorage } from "@/app/lib/storage";
import { useTenant, useToast } from "@/app/hooks";
import { Button } from "@/app/components/ui";

const GoogleMark = () => (
   <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
      <path
         fill="#4285F4"
         d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
         fill="#34A853"
         d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
         fill="#FBBC05"
         d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
      />
      <path
         fill="#EA4335"
         d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
   </svg>
);

interface LoginWithSocialProps {
   onAuthStateChange?: (isAuthenticating: boolean) => void;
}

export const LoginWithSocial = ({ onAuthStateChange }: LoginWithSocialProps) => {
   const locale = useLocale();
   const translation = useTranslations("Auth.login");
   const [isAuthenticating, setIsAuthenticating] = useState(false);
   const { toast } = useToast();
   const router = useRouter();
   const pathname = usePathname();
   const searchParams = useSearchParams();
   const callbackUrl = searchParams.get(MIDDLEWARE.CALLBACK_URL);
   const tenant = useTenant();
   const containerRef = useRef<HTMLDivElement>(null);
   const [googleButtonWidth, setGoogleButtonWidth] = useState(0);

   // Google sizes its button in pixels, so the invisible overlay has to be
   // re-measured to stay exactly on top of the visible one
   useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const observer = new ResizeObserver(([entry]) =>
         setGoogleButtonWidth(Math.round(entry.contentRect.width))
      );
      observer.observe(container);

      return () => observer.disconnect();
   }, []);

   const handleGoogleSuccess = async (credentialResponse: any) => {
      if (isAuthenticating) return;
      setIsAuthenticating(true);
      onAuthStateChange?.(true);

      try {
         const { credential, clientId } = credentialResponse;
         const loginData = {
            access_token: credential,
            g_client_id: clientId,
            grant_type: "google",
            scope: "openid email profile offline_access roles"
         };
         const loginResponse = await fetch(api_url.google_login, {
            method: METHOD_TYPE.POST,
            headers: {
               "Content-Type": "application/json",
               ...(tenant ? { "Tenant-Id": tenant.tenantId.toString() } : {})
            },
            body: JSON.stringify(loginData)
         });

         const data = await loginResponse.json();

         if (!loginResponse.ok) {
            throw new Error(
               data.error ||
                  translation("googleLoginError", {
                     error: JSON.stringify(data)
                  })
            );
         }

         // Validate response structure
         if (!data || !data.user || !data.token) {
            console.error("[Google Login] Invalid response structure:", data);
            throw new Error("Invalid response from server");
         }

         const { token, user } = data;

         // Validate user object
         if (!user.id || !user.email || !user.username) {
            console.error("[Google Login] Invalid user data:", user);
            throw new Error("Invalid user data received");
         }

         setAccessToken(token);

         safeLocalStorage.setItem(
            "user",
            JSON.stringify({
               id: user.id,
               email: user.email,
               userName: user.username,
               avatarUrl: user.avatarUrl
            })
         );

         await new Promise((resolve) => setTimeout(resolve, 500));
         toast({
            variant: "success",
            title: translation("success"),
            description: translation("success"),
            duration: 2_000,
            position: "bottom-center"
         });

         const locale = pathname?.split("/")[1] || "vi";
         if (callbackUrl) {
            window.location.href = callbackUrl;
         } else {
            router.replace(`/${locale}`);
            setTimeout(() => {
               window.location.reload();
            }, 200);
         }
      } catch (error: any) {
         console.error("[Google Login] Error during auth:", error);
         safeLocalStorage.clear();
         removeAccessToken();
         toast({
            variant: "destructive",
            title: translation("error", { error: "" }),
            description: error.message || "Failed to authenticate with Google",
            position: "bottom-center"
         });
         setIsAuthenticating(false);
         onAuthStateChange?.(false);
      }
   };

   const handleGoogleError = () => {
      console.error("[Google Login] OAuth error occurred");

      toast({
         variant: "destructive",
         title: translation("error", { error: "" }),
         description:
            "Google authentication failed. Please check your internet connection and try again.",
         position: "bottom-center"
      });
      setIsAuthenticating(false);
      onAuthStateChange?.(false);
   };

   // Google's button lives in a cross-origin iframe, so it cannot be restyled and
   // cannot be clicked programmatically. It is laid over our own button at zero
   // opacity instead: the user sees ours, but the real click reaches Google's, which
   // keeps the ID-token flow untouched.
   return (
      <div
         ref={containerRef}
         className="relative h-11 w-full rounded-xl focus-within:ring-1 focus-within:ring-ring"
      >
         <Button
            variant="outline"
            size="lg"
            className="pointer-events-none w-full gap-3"
            tabIndex={-1}
            aria-hidden="true"
         >
            <GoogleMark />
            {translation("googleLoginButton")}
         </Button>
         <div className="absolute inset-0 overflow-hidden opacity-0">
            <GoogleLogin
               onSuccess={handleGoogleSuccess}
               onError={handleGoogleError}
               useOneTap={false}
               theme="outline"
               size="large"
               width={googleButtonWidth || undefined}
               text="continue_with"
               shape="rectangular"
               ux_mode="popup"
               locale={locale}
               auto_select={false}
               cancel_on_tap_outside={true}
               context="signin"
               type="standard"
            />
         </div>
      </div>
   );
};
